/**
 * src/components/cliente/EjecucionesCliente.jsx
 * Las ejecuciones de UN cliente: scheduler, disparo manual y lo que el worker
 * está haciendo con lo que se encoló.
 *
 * El 04-09 adelgazó a la mitad. Salieron a `DiagnosticoCliente.jsx` las dos
 * tarjetas de análisis —537 de 993 líneas, el 54 %— porque contestaban otra
 * pregunta: aquí se mira «qué está pasando ahora», allí «por qué los números
 * son los que son». Y se retiró el «Historial de runs», que registraba el mismo
 * hecho que la cola por otra tabla (ver el comentario del ensamblado).
 *
 * Era la página /ejecuciones/:clienteId (pages/Scraping.jsx). El 28-08 (hilo
 * de distribución, decidido por Manuel) se pliega dentro de la ficha del
 * cliente como pestaña «Ejecuciones»: era conceptualmente hija del cliente y
 * vivía separada de él, colgando de la ruta de Ejecuciones. La ruta vieja
 * redirige aquí, así que los enlaces guardados siguen funcionando.
 */
import { useState } from 'react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Table } from '../ui/Table'
import { Plegable } from '../ui/Plegable'
import { useEstado, useTrigger, usePipelineJobs } from '../../hooks/useScraping'
import { useFichaCliente } from '../../hooks/useAdminPanel'
import { EstadoDeLaAccion, varianteDeAccion } from './EstadoDeLaAccion'
import { fechaHoraCL, fechaHoraCortaCL, fechaUTC } from '../../lib/horaChile'

function EstadoSection({ estado }) {
  if (!estado) return null

  function Block({ label, data }) {
    return (
      <div style={{
        padding: '14px', background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        flex: 1,
      }}>
        <div style={{
          fontSize: 'var(--text-micro)', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: '10px',
        }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            ['Estado', <Badge color={data.activo ? 'green' : 'gray'} dot pulse={data.activo}>{data.activo ? 'Activo' : 'Inactivo'}</Badge>],
            ['Frecuencia', <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{data.frecuencia}</span>],
            ['Último run', data.ultimo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-2)' }}>{fechaHoraCL(data.ultimo_run)}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>—</span>],
            ['Próximo run', data.proximo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-2)' }}>{fechaHoraCL(data.proximo_run)}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>—</span>],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>{k}</span>
              {v}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
      <Block label="PRECIOS" data={estado.precios} />
      <Block label="ÓRDENES DE COMPRA" data={estado.ocs} />
    </div>
  )
}

function TriggerPanel({ clienteId, onSuccess, accion }) {
  const trigger = useTrigger(clienteId)
  const [tipo, setTipo] = useState('ambos')
  const [result, setResult] = useState(null)

  // ⚠️ El fondo del elegido se construía como `${t.color}18` —el hex más dos
  // dígitos de alfa—. Eso solo funciona si el color es un hex literal: en
  // cuanto uno pasó a ser un token, `var(--color-warn)18` dejó de ser un color
  // válido y esa opción se quedó SIN fondo al elegirla, en silencio. Cada tipo
  // trae ahora su pareja sólido/tinte explícita, que es lo que el sistema ya
  // tiene y no se puede romper concatenando.
  const TIPOS = [
    { value: 'precios',   label: 'Precios',       solido: 'var(--color-running)',    tinte: 'var(--color-running-soft)',   linea: 'var(--color-running-line)' },
    { value: 'ocs',       label: 'OCs',           solido: 'var(--color-warn)',       tinte: 'var(--color-warn-soft)',      linea: 'var(--color-warn-line)' },
    { value: 'ocs_full',  label: 'OCs histórico', solido: 'var(--color-highlight)',  tinte: 'var(--color-highlight-soft)', linea: 'var(--color-highlight-soft)' },
    { value: 'ambos',     label: 'Ambos',         solido: 'var(--color-accent)', tinte: 'var(--color-accent-soft)',    linea: 'var(--color-accent-soft)' },
    { value: 'dashboard', label: 'Solo dashboard',solido: 'var(--color-neutral)',    tinte: 'var(--color-neutral-soft)',   linea: 'var(--color-neutral-soft)' },
    { value: 'catalogo',  label: 'Solo catálogo', solido: 'var(--color-ok)',         tinte: 'var(--color-ok-soft)',        linea: 'var(--color-ok-line)' },
    { value: 'catalogo_full', label: 'Catálogo desde cero', solido: 'var(--color-error)', tinte: 'var(--color-error-soft)', linea: 'var(--color-error-line)' },
  ]
  const tipoLabel = TIPOS.find(t => t.value === tipo)?.label || tipo

  async function handleTrigger() {
    setResult(null)
    try {
      const data = await trigger.mutateAsync({ tipo })
      setResult({ ok: data.ok, duracion: data.duracion_s, mensaje: data.mensaje })
      onSuccess?.()
    } catch (e) {
      setResult({ ok: false, mensaje: e.response?.data?.detail || 'Error al ejecutar' })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Tipo selector */}
      <div>
        <div style={{
          fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 'var(--space-2)',
        }}>
          TIPO DE SCRAPING
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {TIPOS.map(t => (
            <button
              key={t.value}
              onClick={() => setTipo(t.value)}
              style={{
                padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-base)',
                fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                border: `1px solid ${tipo === t.value ? t.linea : 'var(--color-border)'}`,
                background: tipo === t.value ? t.tinte : 'transparent',
                color: tipo === t.value ? t.solido : 'var(--color-text-2)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tipo === 'ocs_full' && (
          <div style={{
            marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Reconstruye el histórico completo (365 días) de todas las empresas y
            competidores. Más lento que el OCs normal; úsalo para backfill o para
            poblar la Competencia de competidores recién añadidos.
          </div>
        )}
        {tipo === 'catalogo_full' && (
          <div style={{
            marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Igual que «Solo catálogo», pero vuelve a probar también los códigos
            que el catálogo anterior daba por descartados. Tarda bastante más.
            Úsalo cuando sospeches que un run con el portal fallando marcó como
            «no está en la tienda» productos que sí están: ese descarte se
            guarda 30 días y un run normal no lo revisa.
          </div>
        )}
        {tipo === 'catalogo' && (
          <div style={{
            marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Reconstruye el universo de productos del cliente (qué códigos existen
            hoy en sus tiendas) sin tocar precios ni OCs. Es el paso más lento del
            run y solo hace falta una vez por semana, así que va aparte. Úsalo
            cuando el cliente diga que le faltan productos o tras cambiarle las
            tiendas activas.
          </div>
        )}
        {tipo === 'dashboard' && (
          <div style={{
            marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Regenera el dashboard con los datos que ya tiene el cliente, sin
            scraping (segundos). Úsalo tras un cambio de plan o de plantilla para
            que el cliente vea los cambios sin esperar al run de la noche.
          </div>
        )}
      </div>

      {/* Qué de esto lo hace ya el planificador por su cuenta. Encima del
          botón a propósito: debajo se leería después de pulsarlo. */}
      <EstadoDeLaAccion accion={accion} />

      {/* Botón trigger */}
      <Button
        onClick={handleTrigger}
        variant={varianteDeAccion(accion)}
        loading={trigger.isPending}
        icon={
          !trigger.isPending && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )
        }
      >
        {trigger.isPending ? 'Ejecutando pipeline...' : `Ejecutar · ${tipoLabel}`}
      </Button>

      {/* Resultado */}
      {result && (
        <div style={{
          padding: '10px 12px', borderRadius: '7px',
          background: result.ok ? 'var(--color-ok-soft)' : 'var(--color-error-soft)',
          border: `1px solid ${result.ok ? 'var(--color-ok-line)' : 'var(--color-error-line)'}`,
          fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
          color: result.ok ? 'var(--color-ok)' : 'var(--color-error)',
        }}>
          {result.ok ? '✓' : '✗'} {result.mensaje}
          {result.duracion != null && <span style={{ color: 'var(--color-text-3)', marginLeft: 'var(--space-2)' }}>{result.duracion}s</span>}
        </div>
      )}
    </div>
  )
}

function SalidaDelRun({ detalle }) {
  if (!detalle) {
    return (
      <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>
        Este run no dejó salida guardada. Si sigue en «running», aún no ha terminado:
        el detalle se escribe al cerrar.
      </div>
    )
  }
  return (
    <pre style={{
      margin: 0, maxHeight: '320px', overflow: 'auto', whiteSpace: 'pre-wrap',
      wordBreak: 'break-word', fontFamily: 'var(--font-mono)',
      fontSize: 'var(--text-xs)', lineHeight: 1.5, color: 'var(--color-text-2)',
    }}>{detalle}</pre>
  )
}


// ── Cola de trabajos del pipeline ────────────────────────────────────────────
// En Railway el panel NO ejecuta el pipeline: encola un PipelineJob y lo corre el
// worker. Hasta ahora el panel decía «Job encolado» y ahí se acababa la historia:
// si el worker estaba caído, el job se quedaba en `pending` para siempre y desde
// aquí no se veía NADA. Pasó el 28-08 —dos horas de espera a ciegas— y es el §5
// de ERRORES_RECURRENTES, el fallo silencioso. Esta tarjeta lo hace visible, y
// además EXPLICA las dos esperas que son normales y parecen un cuelgue.

// `interrumpido` NO es `error`: un run cortado por un redespliegue del worker no
// falló, y pintarlo de rojo manda a depurar un pipeline que nunca se rompió.
const JOB_COLOR = { ok: 'green', error: 'red', running: 'blue', pending: 'gray',
                    interrumpido: 'yellow' }

function minutosDesde(iso) {
  const d = fechaUTC(iso)
  return d ? Math.floor((Date.now() - d.getTime()) / 60000) : null
}

function lecturaDeLaCola(jobs) {
  const corriendo = jobs.find(j => j.estado === 'running')
  const enCola    = jobs.filter(j => j.estado === 'pending')
  if (corriendo) {
    const min = minutosDesde(corriendo.iniciado_en)
    // El worker devuelve a `pending` los `running` de más de WORKER_MAX_JOB_AGE_MIN
    // (120 por defecto), y mata el subproceso a las 2 h (PIPELINE_TIMEOUT_SECONDS).
    if (min !== null && min >= 120) {
      return ['warn', `El job #${corriendo.id} lleva ${min} min corriendo. El worker
        devuelve a la cola los que pasan de 120 min, así que debería reiniciarse solo;
        si no ocurre, el worker no está vivo.`]
    }
    const extra = enCola.length
      ? ` Los ${enCola.length} en cola esperan a que termine: los trabajos de un mismo
          cliente se ejecutan de uno en uno, a propósito.`
      : ''
    return ['info', `Ejecutando el job #${corriendo.id}${min !== null ? ` desde hace ${min} min` : ''}.${extra}`]
  }
  if (enCola.length) {
    const min = minutosDesde(enCola[enCola.length - 1].creado_en)
    if (min !== null && min >= 5) {
      return ['warn', `${enCola.length} trabajo(s) en cola y ninguno corriendo desde hace
        ${min} min. El worker toma un job cada 10 s, así que esto apunta a que el worker
        no está vivo o no ha desplegado — mira el servicio worker en Railway.`]
    }
    return ['info', `${enCola.length} trabajo(s) en cola. El worker los toma en unos segundos.`]
  }
  return [null, null]
}

// Qué dice la TAPA de la tabla plegada, y si hay que abrirla sola.
//
// Va fuera del componente y sin tocar el DOM: es la parte con reglas, y así se
// razona sobre ella sin navegador.
//
// ⚠️ El `abrir` NO es solo «hay un error». Un worker caído no produce errores:
// produce trabajos que se quedan esperando y una pantalla en calma. Ese es el
// caso que motivó todo esto —el historial que se quitó no llegaba siquiera a
// tener fila—, así que un aviso de la cola abre la tabla igual que un fallo.
export function resumenDeEjecuciones(jobs, nivel) {
  const n = jobs.length
  if (!n) return { texto: 'Sin trabajos encolados', abrir: false }
  const conError = jobs.filter(j => j.estado === 'error').length
  const ultimo = jobs[0]
  const min = minutosDesde(ultimo?.creado_en)
  const cuando = min === null ? null
    : min < 60 ? `hace ${min} min`
    : `hace ${Math.floor(min / 60)} h`
  return {
    texto: [
      // Con tilde en singular y sin ella en plural: «ejecución» /
      // «ejecuciones». Concatenar el sufijo se comía la tilde.
      n === 1 ? '1 ejecución' : `${n} ejecuciones`,
      cuando && `última ${cuando}`,
      conError ? `⚠ ${conError} con error` : null,
    ].filter(Boolean).join(' · '),
    hayError: conError > 0,
    abrir: conError > 0 || nivel === 'warn',
  }
}

function ColaDeTrabajos({ clienteId }) {
  const { data, isLoading } = usePipelineJobs(clienteId)
  // ⚠️ `data = []` por defecto solo cubre `undefined`. Si la respuesta no es una
  // lista —un error serializado, un `{detail: …}`, una API que cambia de forma—
  // `jobs.find` revienta y tumba la página ENTERA, no solo esta tarjeta. Es la
  // misma forma que ya desmontó el Dashboard el 25-08 y la ficha el 26-08, y la
  // cazó el test de humo antes de salir.
  const jobs = Array.isArray(data) ? data : []
  const [nivel, texto] = lecturaDeLaCola(jobs)
  // La celda de resultado sigue siendo un resumen de 8 líneas con scroll: sirve
  // para «ok» y para un traceback corto. Desde el 31-08 un timeout trae además
  // la ÚLTIMA SALIDA del pipeline (hasta 4.000 caracteres), y eso no se lee en
  // una celda. Pulsando la fila se abre entera debajo.
  const [jobAbierto, setJobAbierto] = useState(null)

  if (isLoading) {
    return <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>Cargando…</div>
  }
  if (!jobs.length) {
    return <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>Sin trabajos encolados.</div>
  }

  const resumen = resumenDeEjecuciones(jobs, nivel)

  return (
    <div>
      {/* El aviso de salud NO se pliega nunca. Es la única pieza de esta
          tarjeta que puede estar diciendo que el worker está muerto. */}
      {texto && (
        <div style={{
          marginBottom: 'var(--space-3)', padding: '10px 12px', borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-sm)', lineHeight: 1.5, fontFamily: 'var(--font-ui)',
          color: nivel === 'warn' ? 'var(--color-warn)' : 'var(--color-text-2)',
          background: nivel === 'warn' ? 'var(--color-warn-soft)' : 'var(--color-surface-2)',
          border: `1px solid ${nivel === 'warn' ? 'var(--color-warn-line)' : 'var(--color-border)'}`,
        }}>
          {texto}
        </div>
      )}
      <Plegable
        motivo={resumen.abrir}
        resumen={
          <span data-resumen="ejecuciones"
                style={{ color: resumen.hayError ? 'var(--color-warn)' : undefined }}>
            {resumen.texto}
          </span>
        }
      >
        <Table
          columns={JOB_COLS}
          data={jobs}
          emptyMessage="Sin trabajos encolados"
          onRowClick={j => setJobAbierto(jobAbierto === j.id ? null : j.id)}
          claveExpandida={jobAbierto}
          renderExpandida={j => <SalidaDelRun detalle={j.resultado} />}
        />
      </Plegable>
    </div>
  )
}

// Las marcas de tiempo se abrevian a «HH:MM» cuando son de hoy (Manuel, 31-08,
// 2ª ronda). El `title` lleva SIEMPRE la fecha entera: abreviar no es esconder,
// y basta pasar el ratón para desambiguar sin volver a gastar 200 px por columna.
function _celdaHora(v) {
  const corta = fechaHoraCortaCL(v)
  if (!corta) return '—'
  return <span title={fechaHoraCL(v)}>{corta}</span>
}

const JOB_COLS = [
  { key: 'id', label: '#', width: '60px', mono: true, nowrap: true },
  { key: 'tipo', label: 'Tipo', nowrap: true,
    render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{v}</span> },
  { key: 'estado', label: 'Estado', width: '110px',
    render: v => <Badge color={JOB_COLOR[v] || 'gray'} dot pulse={v === 'running'}>{v}</Badge> },
  { key: 'creado_en', label: 'Encolado', nowrap: true, render: _celdaHora },
  { key: 'iniciado_en', label: 'Iniciado', nowrap: true, render: _celdaHora },
  { key: 'terminado_en', label: 'Terminado', nowrap: true, render: _celdaHora },
  // El tope interno era de 4,5 em —unas 3 líneas y media— cuando la columna
  // medía 190 px: un traceback de tienda caída pedía 182 px de alto y enseñaba
  // 50, con scroll dentro de una celda. Con el ancho recuperado (724 px medidos)
  // el mismo mensaje cabe en dos líneas; el tope sube a 9 em (≈8 líneas), que
  // cubre cualquier resultado real y deja el scroll solo para el monstruo raro.
  // `overflowWrap: anywhere` es la otra mitad: un traceback trae rutas y URLs
  // sin espacios, y sin esto se salen por la derecha en vez de partirse.
  { key: 'resultado', label: 'Resultado',
    render: v => v
      ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                       color: 'var(--color-text-2)', whiteSpace: 'pre-wrap',
                       overflowWrap: 'anywhere', lineHeight: 1.5,
                       display: 'block', maxHeight: '9em', overflow: 'auto' }}>{v}</span>
      : <span style={{ color: 'var(--color-text-3)' }}>—</span> },
]


// ── Censo de convenios marco (OCDS) ──────────────────────────────────────────
// En qué convenios factura el tenant y cuánto. Se resuelve orden a orden contra la
// API OCDS pública, así que va por tramos: cada pulsación resuelve un trozo y lo
// cachea. El botón dice cuántas órdenes faltan en vez de dejar al usuario
// adivinando si terminó.
export function EjecucionesCliente({ clienteId }) {
  const { data: estado, isLoading: loadingEstado } = useEstado(clienteId)
  // La misma ficha que lee la pestaña Diagnóstico: dice si el planificador ya
  // lanza esto solo, con qué frecuencia y cuándo fue la última vez.
  const { data: ficha } = useFichaCliente(clienteId)
  const accionPipeline = (ficha?.acciones || []).find(a => a.clave === 'pipeline') || null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 'var(--space-4)', alignItems: 'start', flexShrink: 0 }}>
          {/* Estado */}
          <Card title="Estado del scheduler" subtitle="Configuración actual">
            {loadingEstado ? (
              <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>Cargando...</div>
            ) : (
              <EstadoSection estado={estado} />
            )}
          </Card>

          {/* Trigger */}
          <Card title="Ejecución manual">
            <TriggerPanel clienteId={clienteId} accion={accionPipeline} />
          </Card>
        </div>

        {/* UNA tabla de ejecuciones, no dos.
            Hasta el 04-09 había aquí «Cola de trabajos» (PipelineJob) e
            «Historial de runs» (RunLog), y registraban el MISMO hecho: en
            producción el scheduler, el disparo manual y el «solo precios»
            llaman los tres a `encolar_pipeline`, y el worker ejecuta
            `run_pipeline.py`, que se crea su propio RunLog. Una ejecución, dos
            filas, dos tablas.

            Se quedó ésta y no la otra por un motivo concreto: **el historial no
            existe hasta que el run arranca**. Con el worker caído, aquí se ven
            los trabajos apilándose en `pending`; allí no se veía NADA, que se
            lee como «no ha pasado nada» en vez de «el worker está muerto» (§5).

            El modelo RunLog NO se ha tocado: sigue alimentando el «último run»
            de la tarjeta de estado y el recuento de `bienvenida_f4`. Lo que se
            retiró es una vista redundante, no un dato. */}
        <Card
          title="Ejecuciones del worker"
          subtitle="El panel encola; el worker ejecuta. Aquí se ve en qué punto está · pulsa una fila para ver su salida completa"
          style={{ flexShrink: 0 }}
        >
          <ColaDeTrabajos clienteId={clienteId} />
        </Card>
    </div>
  )
}
